import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Sparkles, Home, FileText, Calendar, ShieldCheck, User, LogOut, Mic, Clock, Target, GraduationCap, HeartPulse } from "lucide-react";
import Logo from "@/components/Logo";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import AlfredPanel from "@/components/AlfredPanel";
import NotificationBanner from "@/components/NotificationBanner";

const NAV = [
  { to: "/me", label: "Home", icon: Home, end: true },
  { to: "/me/documents", label: "Documents", icon: FileText },
  { to: "/me/leave", label: "Time & Leave", icon: Calendar },
  { to: "/me/timesheet", label: "Timesheet", icon: Clock },
  { to: "/me/reviews", label: "Reviews", icon: Target },
  { to: "/me/learning", label: "Learning", icon: GraduationCap },
  { to: "/me/safety", label: "Safety", icon: HeartPulse },
  { to: "/me/compliance", label: "My Compliance", icon: ShieldCheck },
  { to: "/me/profile", label: "Profile", icon: User },
];

export default function MeShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [askOpen, setAskOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/login"); };
  const initials = (user?.name || user?.email || "?").split(" ").map(x => x[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="me-shell">
      {/* Slim header */}
      <header className="header-dark h-16 px-6 flex items-center justify-between">
        <Link to="/me" className="flex items-center gap-2 text-white">
          <Logo variant="wordmark-white" size={26} />
        </Link>
        <div className="flex items-center gap-3">
          <Button onClick={() => setAskOpen(true)} data-testid="me-ask-alfred-btn"
            className="btn-primary rounded-full gap-2"><Mic className="w-4 h-4" /> Ask Alfred</Button>
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 text-white outline-none" data-testid="me-user-menu">
            <Avatar className="w-8 h-8"><AvatarFallback className="bg-pink text-white text-xs">{initials}</AvatarFallback></Avatar>
            <span className="text-sm hidden sm:block">{user?.name}</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={doLogout} data-testid="me-logout-btn"><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </header>
      <div className="max-w-6xl mx-auto p-6 lg:p-10 grid lg:grid-cols-[220px_1fr] gap-8">
        {/* Sub-nav */}
        <aside className="space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              data-testid={`me-nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${isActive ? "bg-pink/10 text-pink font-semibold" : "text-neutral-600 hover:bg-neutral-100"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </aside>
        <main><NotificationBanner /><Outlet /></main>
      </div>
      <AlfredPanel open={askOpen} onOpenChange={setAskOpen} />
    </div>
  );
}
